import type { GridData, ImageDimensions, ShardMap, RegionShard } from '../lib/types'
import { getColorPalette, getOliveColorPalette } from '../lib/colors'
import { updateGridPixel } from './utils'
import { getRandomShard } from '../shards'
import { computeSaturatedGrid } from './saturate'

export function computeSaturatedShardedGrids(
  dimensions: ImageDimensions,
  ignoreBlocks: boolean[][],
  blockSize: number,
): { oliveGrid: GridData; colorGrid: GridData; shardMap: ShardMap } {
  const colorGrid = computeSaturatedGrid(dimensions, ignoreBlocks, blockSize, getColorPalette())
  const oliveGrid = computeSaturatedGrid(
    dimensions,
    ignoreBlocks,
    blockSize,
    getOliveColorPalette(),
  )

  const spacing = 10
  const shards: RegionShard[] = []
  let shardId = 0

  const yCount = Math.ceil(dimensions.height / (spacing * blockSize))
  const xCount = Math.ceil(dimensions.width / (spacing * blockSize))

  for (let gy = 0; gy <= yCount; gy += 1) {
    for (let gx = 0; gx <= xCount; gx += 1) {
      let adjustedBlocksX = randomBetween(0, Math.floor(spacing / 4)) * blockSize
      let adjustedBlocksY = randomBetween(0, Math.floor(spacing / 4)) * blockSize
      if (gx % 2 !== 0) {
        adjustedBlocksY += blockSize * (spacing / 2)
      }
      adjustedBlocksY -= blockSize * (spacing / 2)
      adjustedBlocksX -= blockSize * (spacing / 2)

      const posY = gy * (blockSize * spacing) + adjustedBlocksY
      const posX = gx * (blockSize * spacing) + adjustedBlocksX

      // Select a random shard shape
      const shardShape = getRandomShard()

      // Create a shard
      const shard: RegionShard = {
        ...shardShape,
        regionId: 0, // These are standalone shards, not tied to regions
        offsetX: Math.floor(posX / blockSize),
        offsetY: Math.floor(posY / blockSize),
        size: blockSize,
        id: shardId++,
      }
      shards.push(shard)

      // Place the shard
      for (let i = 0; i < shardShape.blocks.length; i++) {
        const block = shardShape.blocks[i]!
        const blockX = posX + block.x * blockSize
        const blockY = posY + block.y * blockSize
        const { r, g, b } = block.color

        fillGridBlocks(oliveGrid, blockX, blockY, blockSize, dimensions, { r, g, b })
        fillGridBlocks(colorGrid, blockX, blockY, blockSize, dimensions, { r, g, b })
      }
    }
  }

  return { oliveGrid, colorGrid, shardMap: { shards } }
}

function fillGridBlocks(
  grid: GridData,
  startX: number,
  startY: number,
  blockSize: number,
  dimensions: ImageDimensions,
  {
    r,
    g,
    b,
  }: {
    r: number
    g: number
    b: number
  },
) {
  for (let py = 0; py < blockSize; py++) {
    for (let px = 0; px < blockSize; px++) {
      const blockX = startX + px
      const blockY = startY + py

      if (blockY >= 0 && blockY < dimensions.height && blockX >= 0 && blockX < dimensions.width) {
        updateGridPixel(grid, blockX, blockY, { r, g, b, a: 255 })
      }
    }
  }
}

function randomBetween(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}
